import { fetchPlayers } from '@api-service/players';
import type { Player } from '@api-service/players';

/**
 * Fetches full Player objects (with portrait imageUrl) by their UUIDs. The
 * team-rating squad only returns UUIDs, so the read-only reveal pitch and
 * the transfer OUT token resolve portraits through the players endpoint.
 * Prefetched during the processing step so the reveal has no loading flash.
 *
 * Predictions are skipped — only portrait/identity fields are read here — for
 * a faster response.
 */
export const fetchSquadPlayers = async (
  uuids: string[]
): Promise<Map<string, Player>> => {
  const ids = uuids.filter(Boolean);
  if (ids.length === 0) return new Map();

  const response = await fetchPlayers({
    ids: ids.join(','),
    limit: ids.length,
    exclude: 'predictions'
  });
  return new Map(response.data.map((player) => [player.id, player]));
};

/**
 * Fetches full Player objects by their Opta ids, in no guaranteed order.
 *
 * The sibling of `fetchSquadPlayers` for callers holding ids that outlive a
 * season. Player UUIDs are re-minted every season, so any list checked into the
 * repo has to be written in Opta ids and resolved through this.
 *
 * Returns a list rather than a Map: the caller that needs this is choosing from
 * the players, not looking one up.
 */
export const fetchPlayersByOptaIds = async (
  optaIds: string[]
): Promise<Player[]> => {
  const ids = optaIds.filter(Boolean);
  if (ids.length === 0) return [];

  const response = await fetchPlayers({
    optaIds: ids.join(','),
    limit: ids.length,
    exclude: 'predictions'
  });
  return response.data;
};
